import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  AdvancedFilters,
  FeasibilityAdvancedFilterType,
  FeasibilityFilterCriteriaGroup,
  SurveyLocaleData,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyCreationPricingState,
  SurveyCreationInsightsActions,
} from './insights';

import { SurveyCreationActions } from './audience/survey-creation-audience.actions';
import { SurveyCreationReferenceActions } from './reference/survey-creation-reference.actions';

@Injectable({ providedIn: 'root' })
export class SurveyCreationActionsFacade {
  private readonly _store = inject(Store);

  public init(surveyId: Nullable<number>): void {
    this._store.dispatch(SurveyCreationActions.init({ surveyId }));
  }

  public createStandardSurvey(): void {
    this._store.dispatch(SurveyCreationActions.createStandardSurvey());
  }

  public updateMetadata(metadata: SurveyMetadataFormState): void {
    this._store.dispatch(SurveyCreationActions.updateMetadata({ metadata }));
  }

  public updateQuotas(quotas: SurveyQuotasFormState): void {
    this._store.dispatch(SurveyCreationActions.updateQuotas({ quotas }));
  }

  public updateQualifications(
    qualifications: SurveyQualificationsFormState,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.updateQualifications({ qualifications }),
    );
  }

  public saveSurvey(): void {
    this._store.dispatch(SurveyCreationActions.saveSurvey());
  }

  public reset(): void {
    this._store.dispatch(SurveyCreationActions.reset());
  }

  public uploadLocationCodes(
    countryCode: string,
    surveyId: number,
    qualificationCode: number,
    qualificationName: string,
    file: File,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.uploadLocationCodes({
        countryCode,
        surveyId,
        qualificationCode,
        qualificationName,
        file,
      }),
    );
  }

  public clearLocationCodes(
    qualificationCode: number,
    qualificationName: string,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.clearLocationCodes({
        qualificationCode,
        qualificationName,
      }),
    );
  }

  public downloadLocationCodesTemplate(
    countryCode: string,
    qualificationCode: number,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.downloadLocationCodesTemplate({
        countryCode,
        qualificationCode,
      }),
    );
  }

  public downloadValidatedLocationFile(qualificationCode: number): void {
    this._store.dispatch(
      SurveyCreationActions.downloadValidatedLocationFile({
        qualificationCode,
      }),
    );
  }

  public loadQualifications(localeData: SurveyLocaleData[]): void {
    for (const eachLocaleData of localeData) {
      this._store.dispatch(
        SurveyCreationReferenceActions.loadQualifications(eachLocaleData),
      );
    }
  }

  public loadQuestionCategories(): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestionCategories(),
    );
  }

  public loadQuestions(search: string, categoryIds: number[]): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestions({ search, categoryIds }),
    );
  }

  public loadLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadLocationConditions({
        dbQualificationCode,
        locale,
      }),
    );
  }

  public toggleManualProcessing(companyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationActions.toggleManualProcessing({
        companyId,
        enabled,
      }),
    );
  }

  public recalculateFeasibility(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.recalculateFeasibility(),
    );
  }

  public applyCpiPricing(pricing: SurveyCreationPricingState): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.applyCpiPricing(pricing),
    );
  }

  public findSurveys(
    filterType: FeasibilityAdvancedFilterType,
    criteria: FeasibilityFilterCriteriaGroup[],
    dateRange: Nullable<readonly Date[]>,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.findSurveys({
        filterType,
        criteria,
        dateRange,
      }),
    );
  }

  public getRespondentCounts(
    filterType: FeasibilityAdvancedFilterType,
    allSurveyGroupIds: number[],
    statuses: number[],
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.getRespondentCounts({
        filterType,
        allSurveyGroupIds,
        statuses,
      }),
    );
  }

  public searchCriteriaSurveys(
    searchType: 'billingNumber' | 'surveyTitle',
    value: string,
    dateRange: Nullable<readonly Date[]>,
    countryCode?: string,
    languageCode?: string,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.searchCriteriaSurveys({
        searchType,
        value,
        dateRange,
        countryCode,
        languageCode,
      }),
    );
  }

  public searchGroupSurveys(
    value: string,
    dateRange: Nullable<readonly Date[]>,
    countryCode?: string,
    languageCode?: string,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.searchGroupSurveys({
        value,
        dateRange,
        countryCode,
        languageCode,
      }),
    );
  }

  public applyAdvancedFilters(filters: AdvancedFilters): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.applyAdvancedFilters({ filters }),
    );
  }

  public toggleExcludeFromFeasibleAudience(
    excludeFromFeasibleAudience: boolean,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.toggleExcludeFromFeasibleAudience({
        excludeFromFeasibleAudience,
      }),
    );
  }
}
